import { useState } from 'react';

import { ApiClient, ApiError } from '../api/client';
import type { IssuedCode, LookupResult } from '../api/types';
import { describeRemaining, formatToman, subscriptionTone, toJalali, toPersianDigits } from '../format';
import { copyText } from '../clipboard';

/* The screen used dozens of times a day: a customer phones, reads out the code
 * on their screen, and everything about them appears.
 *
 * Two actions sit side by side, and which one to use is the whole job:
 *   «همان کد قبلی»  — they lost their licence. Same code, same expiry, no charge.
 *   «تمدید یک‌ساله» — they are buying more time.
 * Picking the wrong one either charges for nothing or gives a year away, so they
 * are labelled by consequence rather than by mechanism. */

interface Props {
  api: ApiClient;
  onAuthLost: () => void;
}

export function LookupScreen({ api, onAuthLost }: Props) {
  /* What was TYPED, which is not the serial and must never be used as one. It
     can be a device code, a chassis number, a phone number or a name. */
  const [serial, setSerial] = useState('');
  /* Every car the query matched. A phone number or a name can belong to more
     than one, and silently taking the first would hide the others from whoever
     is trying to renew them. */
  const [matches, setMatches] = useState<LookupResult[] | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [issued, setIssued] = useState<IssuedCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  /* VIN editing. `null` means "not editing" — distinct from an empty string,
   * which is a deliberate request to clear the field. */
  const [vinDraft, setVinDraft] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthenticated) return onAuthLost();
      setError(e instanceof Error ? e.message : 'خطای ناشناخته');
    } finally {
      setBusy(false);
    }
  };

  const search = () =>
    run(async () => {
      setIssued(null);
      setVinDraft(null);
      const found = await api.lookup(serial.trim());
      setMatches(found.matches);
      /* One hit goes straight through; several wait to be chosen. */
      setResult(found.matches.length === 1 ? found.matches[0] : null);
    });

  /* Every action below acts on the SELECTED vehicle's serial, never on the text
     in the box. Sending the typed text was the original bug: the server treated
     a device code as a serial and answered about a car that does not exist. */
  const selectedSerial = () => {
    if (!result) throw new Error('هیچ خودرویی انتخاب نشده است');
    return result.serialno;
  };

  const refresh = async () => {
    const again = await api.lookup(selectedSerial());
    setResult(again.matches[0] ?? null);
  };

  const saveVin = () =>
    run(async () => {
      const value = (vinDraft ?? '').trim();
      await api.setVin(selectedSerial(), value.length === 0 ? null : value);
      setVinDraft(null);
      await refresh();
    });

  const recover = () =>
    run(async () => {
      setIssued(await api.recoverCode(selectedSerial()));
      await refresh();
    });

  const renew = () =>
    run(async () => {
      setIssued(await api.renew(selectedSerial()));
      await refresh();
    });

  const copy = async (text: string) => {
    /* copyText, not navigator.clipboard: the panel is served over plain HTTP on
     * a LAN IP, where the Clipboard API does not exist. */
    setCopied(await copyText(text));
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div>
      <div className="card">
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void search();
          }}
        >
          <div className="grow">
            <input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="کد دستگاه، شماره شاسی، موبایل یا نام مالک"
              autoFocus
              dir="ltr"
              className="mono"
            />
          </div>
          <button type="submit" className="primary" disabled={busy || serial.trim().length < 8}>
            جستجو
          </button>
        </form>
      </div>

      {error && <div className="alert error">{error}</div>}

      {/* Nothing matched. It used to also print a "device code" here — derived
          from the typed text as if it were a serial — which was a real-looking
          code for a car that has never existed. Saying only that nothing was
          found is both true and more useful. */}
      {matches !== null && matches.length === 0 && (
        <div className="alert info">
          خودرویی با «{serial.trim()}» پیدا نشد. کد دستگاه، شماره شاسی، موبایل یا نام مالک را
          امتحان کنید.
        </div>
      )}

      {/* More than one car — a phone number or a name does that. */}
      {matches !== null && matches.length > 1 && !result && (
        <div className="card">
          <div className="muted" style={{ marginBottom: 10 }}>
            {toPersianDigits(matches.length)} خودرو پیدا شد — یکی را انتخاب کنید:
          </div>
          <table className="grid">
            <tbody>
              {matches.map((m) => (
                <tr key={m.serialno}>
                  <td className="mono">{m.deviceCode}</td>
                  <td>{m.owner?.fullName ?? '—'}</td>
                  <td className="muted" dir="ltr">
                    {m.owner ? toPersianDigits(m.owner.phone) : ''}
                  </td>
                  <td className="muted" dir="ltr">{m.device?.vin ?? ''}</td>
                  <td>
                    {m.subscription
                      ? describeRemaining(m.subscription.daysRemaining, m.subscription.expired)
                      : 'بدون اشتراک'}
                  </td>
                  <td>
                    <button onClick={() => setResult(m)}>انتخاب</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result?.known && (
        <div className="card">
          <div className="spread">
            <div>
              <div style={{ fontSize: 20 }}>{result.owner?.fullName ?? '—'}</div>
              <div className="muted">
                {result.owner ? toPersianDigits(result.owner.phone) : 'مالکی ثبت نشده'}
                {result.owner?.job ? ` · ${result.owner.job}` : ''}
              </div>
            </div>
            <div className="center">
              <div className="muted" style={{ fontSize: 13 }}>کد دستگاه</div>
              <div className="mono" style={{ fontSize: 20 }}>{result.deviceCode}</div>
            </div>
          </div>

          <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '18px 0' }} />

          {/* The chassis number: the only identifier the owner can read off their
              own car. The head unit's serial is inside the dashboard, so on a
              support call this is what they can actually give you. */}
          <div className="spread" style={{ marginBottom: 18 }}>
            <div>
              <div className="muted" style={{ fontSize: 13 }}>شماره شاسی (VIN)</div>
              {vinDraft === null ? (
                <div className="mono" style={{ fontSize: 17 }}>
                  {result.device?.vin ?? <span className="muted">ثبت نشده</span>}
                </div>
              ) : (
                <input
                  value={vinDraft}
                  onChange={(e) => setVinDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void saveVin();
                    if (e.key === 'Escape') setVinDraft(null);
                  }}
                  dir="ltr"
                  className="mono"
                  maxLength={32}
                  autoFocus
                  placeholder="۱۷ کاراکتر — خالی بگذارید تا پاک شود"
                />
              )}
            </div>
            <div className="row">
              {vinDraft === null ? (
                <button onClick={() => setVinDraft(result.device?.vin ?? '')} disabled={busy}>
                  {result.device?.vin ? 'ویرایش' : 'ثبت'}
                </button>
              ) : (
                <>
                  <button onClick={() => setVinDraft(null)} disabled={busy}>
                    انصراف
                  </button>
                  <button className="primary" onClick={() => void saveVin()} disabled={busy}>
                    ذخیره
                  </button>
                </>
              )}
            </div>
          </div>

          <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '18px 0' }} />

          {result.subscription ? (
            <div className="spread">
              <div>
                <span
                  className={`pill ${subscriptionTone(
                    result.subscription.daysRemaining,
                    result.subscription.expired,
                  )}`}
                >
                  {describeRemaining(result.subscription.daysRemaining, result.subscription.expired)}
                </span>
                <div className="muted" style={{ marginTop: 8 }}>
                  پایان اشتراک: {toJalali(result.subscription.endDate)}
                </div>
                {/* The server records what was sold, not what reached the car. */}
                {!result.subscription.confirmed && (
                  <div className="muted" style={{ fontSize: 13 }}>
                    تایید نشده — معلوم نیست کد روی خودرو وارد شده باشد
                  </div>
                )}
              </div>
              <div className="row">
                <button onClick={() => void recover()} disabled={busy}>
                  همان کد قبلی
                </button>
                <button className="primary" onClick={() => void renew()} disabled={busy}>
                  تمدید یک‌ساله
                </button>
              </div>
            </div>
          ) : (
            <div className="muted">اشتراکی برای این خودرو ثبت نشده است.</div>
          )}
        </div>
      )}

      {issued && (
        <div className="card">
          <div className="muted" style={{ marginBottom: 10 }}>
            {issued.reused
              ? 'همان کد قبلی — اشتراک با همان تاریخ پایان برمی‌گردد'
              : `کد جدید — تا ${toJalali(issued.endDate)}`}
          </div>
          <div className="bigcode">{issued.code}</div>
          <div className="row" style={{ marginTop: 14, justifyContent: 'space-between' }}>
            <span className="muted">
              {describeRemaining(issued.daysRemaining, false)}
              {issued.amount !== '0' && ` · ${formatToman(issued.amount)}`}
            </span>
            <div className="row">
              <button onClick={() => void copy(issued.code)}>{copied ? 'کپی شد' : 'کپی کد'}</button>
              {/* Opens the phone's SMS app with the text ready — there is no SMS
                  gateway, the code is sent by hand. */}
              <a
                href={`sms:?&body=${encodeURIComponent(
                  `کد اشتراک اوتلندر شما:\n${issued.code}\nمعتبر تا ${toJalali(issued.endDate)}`,
                )}`}
              >
                <button type="button">ارسال با پیامک</button>
              </a>
            </div>
          </div>
          {issued.alsoNeedsRenewal && (
            <div className="alert info" style={{ marginTop: 14, marginBottom: 0 }}>
              اشتراک این خودرو پایان یافته است — این کد لانچر را برمی‌گرداند ولی برای فعال شدن، تمدید هم لازم است.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
